import { getRumAgent } from './registry';

// W3C trace context on katibe calls only, so the browser's RUM event and katibe's own APM
// transaction/error share one trace.id. Not the agent's distributedTracingOrigins: that matches
// whole origins and would add the header to every apigw.paziresh24.com call.
// Only apigw is matched: its katibe routes allow traceparent in CORS (checked 2026-10-10), and
// apiGatewayClient always sends accept-timezone, so these calls already have a preflight.
const KATIBE_URL = /^https:\/\/apigw\.paziresh24\.com\/katibe\//;
// katibe-core/-adaptor/-modules sample 10% and follow the caller's flag; keep their rate.
const KATIBE_SAMPLE_RATE = 0.1;

const TRACE_ID = /^[\da-f]{32}$/;
const SPAN_ID = /^[\da-f]{16}$/;

// Not in the agent's typings, but set on every transaction (sampled or not).
type TraceIds = { id?: string; traceId?: string; sampled?: boolean };

export interface RequestLike {
  url?: string;
  baseURL?: string;
  method?: string;
}

const fullUrl = ({ url = '', baseURL = '' }: RequestLike) =>
  /^https?:\/\//.test(url) ? url : `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;

/** traceparent for a katibe request (axios config shape), or undefined for anything else. */
export function katibeTraceparent(request: RequestLike, random: () => number = Math.random): string | undefined {
  const url = fullUrl(request);
  if (!KATIBE_URL.test(url)) return undefined;
  const apm = getRumAgent();
  if (!apm) return undefined;
  try {
    let tr = apm.getCurrentTransaction() as TraceIds | undefined;
    if (!tr) {
      // What the agent itself does for a request outside any transaction: a managed
      // http-request transaction, which then takes the request's span and ends with it.
      const name = `${(request.method || 'get').toUpperCase()} ${url.split('?')[0]}`;
      tr = apm.startTransaction(name, 'http-request', { managed: true }) as TraceIds | undefined;
    }
    if (!tr?.traceId || !tr.id || !TRACE_ID.test(tr.traceId) || !SPAN_ID.test(tr.id)) return undefined;
    const sampled = tr.sampled || random() < KATIBE_SAMPLE_RATE;
    return `00-${tr.traceId}-${tr.id}-${sampled ? '01' : '00'}`;
  } catch {
    // Monitoring must never break the request.
    return undefined;
  }
}
